import { type BreadcrumbProps, Tag } from '@blueprintjs/core'
import classNames from 'classnames'
import { Dialog } from '../ui/dialog'
import { format } from 'date-fns'
import { makeAutoObservable } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
    EventParentTypes,
    Filters,
    KeywordMatchTypes,
    PerformanceFilters,
    TableName,
} from '../../config'
import { useKdpFilters } from '../../hooks/useKdpFilters'
import { cents, percentage } from '../../util/formatters'
import { parseExpression } from '../../util/parse_expression'
import type { TableProps } from '../ad_groups/AdGroupsTable'
import { AmazonLink } from '../amazon_link'
import { Layout } from '../Layout'
import { NameText } from '../table/NameText'
import type { CellProps } from '../table2/ad_grid_columns'
import { type AgGridColumnDef, AgGridTable, DummyHeader } from '../table2/AgGridTable'
import { generateMatchType } from '../trees/KeywordsTree'

const defaultFilters = [
    Filters.PortfolioId,
    Filters.CampaignId,
    Filters.AdGroupId,
    Filters.StateWithSplit,
    Filters.KeywordMatchType,
    Filters.Bid,
    Filters.TargetACOS,
    ...PerformanceFilters,
]

export interface Log {
    source_type_id: number
    source_id: number
    source?: {
        name: string
        deleted_at: string | null
    }
    target_type_id: number
    target_id: number
    created_at: string
    event: any
    target: any
    type: any
}

const mutableFields: string[] = ['state', 'bid']

class LogsStore {
    modal: any | null = null

    constructor() {
        makeAutoObservable(this)
    }

    clearModal() {
        this.modal = null
    }

    setModal(event: any | null, type: any | null) {
        this.modal = { event, type }
    }
}

const logsStore = new LogsStore()

export const LogsTable = observer(
    ({
        nested,
        store,
        savedFiltersStore,
        tableHooks,
        leadingActions,
    }: TableProps<Log> & { leadingActions?: React.ReactNode }): JSX.Element => {
        const filters = useKdpFilters(defaultFilters)

        const { params } = tableHooks

        const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }]

        const cols3 = useMemo(() => {
            return newColumns()
        }, [])

        return (
            <Layout
                breadcrumbs={breadcrumbs}
                primary={TableName.Logs}
                nested={nested}
                params={params}
            >
                <AgGridTable
                    name={TableName.Logs}
                    columns={cols3}
                    tableHooks={tableHooks}
                    store={store}
                    className="table-header-top-border"
                    showHeader={false}
                    savedFiltersStore={savedFiltersStore}
                    filters={filters}
                    leadingActions={leadingActions}
                    columnSelection
                    mutableFields={mutableFields}
                    nested={nested}
                />
                <Dialog
                    isOpen={!!logsStore.modal}
                    onClose={() => logsStore.clearModal()}
                    className="p-4"
                >
                    {generateEventContent(logsStore.modal?.event, logsStore.modal?.type)}
                </Dialog>
            </Layout>
        )
    },
)

const newColumns = <T,>(): AgGridColumnDef<Log>[] => {
    const cols: AgGridColumnDef<Log>[] = [
        {
            field: 'created_at',
            headerName: 'Date',
            valueFormatter: ({ value }) => format(new Date(value), 'M/d/yy H:mm a'),
            headerComponent: DummyHeader,
            width: 150,
        },
        {
            field: 'source',
            headerName: 'Source',
            cellRenderer: SourceCell,
            headerComponent: DummyHeader,
            flex: 1,
        },
        {
            field: 'target_id',
            headerName: 'Effected Entities',
            cellRenderer: EffectedEntities,
            headerComponent: DummyHeader,
            flex: 1,
        },
        {
            field: 'event',
            headerName: 'Event',
            cellRenderer: Event,
            headerComponent: DummyHeader,
            flex: 1,
        },
    ]

    return cols
}

const SourceCell = ({ data }: CellProps<Log>) => {
    switch (data.source_type_id) {
        case EventParentTypes.SmartBid:
            return 'Smart Bids'
        case EventParentTypes.Promotion:
            if (data.source?.deleted_at == null) {
                return (
                    <span className="whitespace-nowrap overflow-x-hidden w-full">
                        <Link to={`/smart-campaigns/${data.source_id}`}>
                            Smart Campaign - {data.source?.name}
                        </Link>
                    </span>
                )
            } else {
                return <span>[Deleted] Smart Campaign - {data.source?.name}</span>
            }
        default:
            console.warn('Cannot render Source for type:', data.source_type_id)
            return null
    }
}

export const EffectedEntities = ({
    data,
    context: {
        userHook: { screenshotMode },
    },
}: CellProps<Log>) => {
    switch (data.target_type_id) {
        case EventParentTypes.KEYWORD:
            return (
                <div style={{ lineHeight: '21px' }}>
                    <div>
                        <Link to={`/campaigns/${data.target.campaign.campaign_id}`}>
                            <NameText
                                screenshotMode={screenshotMode}
                                className={classNames({ blur: screenshotMode })}
                            >
                                {data.target.campaign.name}
                            </NameText>
                        </Link>
                    </div>
                    <div>
                        <Link to={`/ad-groups/${data.target.ad_group.ad_group_id}`}>
                            <NameText
                                screenshotMode={screenshotMode}
                                className={classNames({ blur: screenshotMode })}
                            >
                                {data.target.ad_group.name}
                            </NameText>
                        </Link>
                    </div>
                    <div className={classNames({ blur: screenshotMode })}>
                        Keyword "{data.target.keyword_text}"
                    </div>
                </div>
            )
        case EventParentTypes.TARGETS:
            return (
                <div style={{ lineHeight: '21px' }}>
                    <div>
                        <Link to={`/campaigns/${data.target.campaign.campaign_id}`}>
                            <NameText
                                screenshotMode={screenshotMode}
                                className={classNames({ blur: screenshotMode })}
                            >
                                {data.target.campaign.name}
                            </NameText>
                        </Link>
                    </div>
                    <div>
                        <Link to={`/ad-groups/${data.target.ad_group.ad_group_id}`}>
                            <NameText
                                screenshotMode={screenshotMode}
                                className={classNames({ blur: screenshotMode })}
                            >
                                {data.target.ad_group.name}
                            </NameText>
                        </Link>
                    </div>
                    <div className={classNames({ blur: screenshotMode })}>
                        Target - {parseExpression(data.target)}
                    </div>
                </div>
            )
        case EventParentTypes.CAMPAIGN:
            return (
                <div style={{ lineHeight: '21px' }}>
                    <div>
                        <Link to={`/campaigns/${data.target.campaign_id}`}>
                            <NameText
                                screenshotMode={screenshotMode}
                                className={classNames({ blur: screenshotMode })}
                            >
                                {data.target.name}
                            </NameText>
                        </Link>
                    </div>
                </div>
            )
        case EventParentTypes.AD_GROUP:
            return (
                <div style={{ lineHeight: '21px' }}>
                    <div>
                        <Link to={`/campaigns/${data.target.campaign.campaign_id}`}>
                            <NameText
                                screenshotMode={screenshotMode}
                                className={classNames({ blur: screenshotMode })}
                            >
                                {data.target.campaign.name}
                            </NameText>
                        </Link>
                    </div>
                    <div>
                        <Link to={`/ad-groups/${data.target.ad_group_id}`}>
                            <NameText
                                screenshotMode={screenshotMode}
                                className={classNames({ blur: screenshotMode })}
                            >
                                {data.target.name}
                            </NameText>
                        </Link>
                    </div>
                </div>
            )
        default:
            console.warn('Cannot render Source for type:', data.target_type_id)
            return null
    }
}

const Event = ({ data }: CellProps<Log>) => {
    const { event, source_type_id, type } = data
    switch (source_type_id) {
        case EventParentTypes.SmartBid:
            const dir = event.new_bid > event.old_bid ? 'Increased' : 'Decreased'
            const dir2 = event.new_bid > event.old_bid ? 'increase' : 'decrease'
            return (
                <div style={{ lineHeight: '21px' }}>
                    {dir} bid from
                    {` ${cents(event.old_bid)} to ${cents(event.new_bid)}`}. Trying to {dir2} ACoS
                    from
                    {` ${percentage(event.current_acos)} to target ${percentage(
                        event.target_acos,
                    )} `}
                </div>
            )
        case EventParentTypes.Promotion:
            const count = event.length
            // const content = generateEventContent(event, type)

            return (
                <div
                    className="fake-link"
                    onClick={() => logsStore.setModal(event, type)}
                    style={{ lineHeight: '21px' }}
                >
                    <div>
                        Created {count} {eventTypeString(type)}s
                    </div>
                </div>
            )
        default:
            console.warn('Cannot render Event for source type:', source_type_id)
            return null
    }
}

const generateEventContent = (event = [], type) => {
    if (type == EventParentTypes.TARGETS || type == EventParentTypes.NegativeTargets) {
        return (
            <ul>
                {event.map((target) => (
                    <li className="mb-1" key={target.target_id + ':' + target.expression_value}>
                        <span className="ml-1">
                            <Tag
                                className="mr-1"
                                style={{ display: 'inline-flex' }}
                                intent="success"
                            >
                                <b>ASIN</b>
                            </Tag>
                            <AmazonLink asin={target.expression_value} screenshotMode={false} />
                            {type === EventParentTypes.TARGETS
                                ? ` - bid ${cents(target.bid)}`
                                : null}
                        </span>
                    </li>
                ))}
            </ul>
        )
    }

    return (
        <ul>
            {event.map((kw) => (
                <li className="mb-1" key={kw.keyword_id + ':' + kw.keyword_text}>
                    {generateMatchType(matchTypeString(kw.match_type, type))}
                    <span className="ml-1">
                        {kw.keyword_text}
                        {type === EventParentTypes.KEYWORD ? ` - bid ${cents(kw.bid)}` : null}
                    </span>
                </li>
            ))}
        </ul>
    )
}

const matchTypeString = (matchType, eventType) => {
    switch (eventType) {
        case EventParentTypes.KEYWORD:
        case EventParentTypes.NegativeKeywords:
            return matchType
        case EventParentTypes.CampaignNegativeKeywords:
            switch (matchType) {
                case KeywordMatchTypes.Exact:
                    return 'negativeExact'
                case KeywordMatchTypes.Phrase:
                    return 'negativePhrase'
                default:
                    console.error('Cannot resolve match type string for:', matchType, eventType)
                    return null
            }
    }
}

const eventTypeString = (eventType) => {
    switch (eventType) {
        case EventParentTypes.KEYWORD:
            return 'keyword'
        case EventParentTypes.NegativeKeywords:
            return 'negative keyword'
        case EventParentTypes.CampaignNegativeKeywords:
            return 'negative campaign keyword'
        case EventParentTypes.TARGETS:
            return 'product target'
        case EventParentTypes.NegativeTargets:
            return 'negative product target'
    }
}
